import { useState, useEffect, useContext } from "react";
import { ConfirmationPopup } from "./ConfirmationPopup";
import DataGridHeaderItem from "./DataGridHeaderItem";
import moment from "moment";
import { NumericFormat } from "react-number-format";
import { TextBox } from "./TextBox";
import { TextItem } from "./TextItem";
import { apiDownloadFile } from "../../services/FilesService";
import { Picture } from "./Picture";
import AddButtonInTopBar from "../AddButtonInTopBar";
import Button from "./Button";
import { useTranslation } from "react-i18next";
import TranslationContext from "../../providers/TranslationContext";

interface Props {
  rows: any[];
  cols: any[];
  loading: boolean;
  showEditButton?: boolean;
  showDeleteButton?: boolean;
  handleEditAction?: any;
  handleDeleteAction?: any;
  showCustomButton?: boolean;
  handleCustomAction?: any;
  customButtonText?: string;
  onDeleteRow?: any;
  setPictureUploadPopupData?: any;
  additionalClassName?: string;
}

require("./DataGrid.css");

const DataGrid = (props: Props) => {
  const { t } = useTranslation();
  const [deleteConfirmationVisible, setDeleteConfirmationVisible] =
    useState(false);

  const onDeleteButtonClick = () => {
    setDeleteConfirmationVisible(true);
  };

  const [rows, setRows] = useState(props.rows);
  const [cols, setCols] = useState(props.cols);
  const [sortCol, setSortCol] = useState(null);
  const [multiSelectFilters, setMultiSelectFilters] = useState(Array());
  const [textFilters, setTextFilters] = useState(Array());
  const [activeRow, setActiveRow] = useState(null);

  useEffect(() => {

    const newCols = props.cols;
    newCols.forEach((c) => {
      if (c.filterable && c.filterType === "dropdown") {
        const arrStrings = props.rows
          .map((p: any) => p[c.dataField])
          .filter((value, index, self) => {
            return self.indexOf(value) === index && value;
          });
        const arr = arrStrings.map((a, i) => {
          return {
            name: a,
            id: i,
            selected: false,
          };
        });
        c.filters = arr;
      }
    });
    setCols(newCols);
    let newRows = props.rows;
    if (sortCol) {
      newRows = doSort(sortCol, newRows);
    }
    if (textFilters) {
      newRows = filterRowsByTextFilters(textFilters, newRows);
      if (multiSelectFilters.length) {
        newRows = filterByMultiSelectFilters(multiSelectFilters, newRows);
      }
    }
    setRows(newRows);
  }, [props.rows, props.cols]);

  const handleSort = (col: any) => {
    let newRows = rows;
    newRows = doSort(col, newRows);
    setRows([...newRows]);
    setSortCol(col);
  };
  const doSort = (col: any, rows: any) => {
    const newRows = rows.sort((a: any, b: any) => {
      const aIVal = a[col.dataField] ? a[col.dataField] : "";
      const bIVal = b[col.dataField] ? b[col.dataField] : "";
      const aVal =
        !isNaN(parseFloat(aIVal)) && isFinite(aIVal)
          ? aIVal
          : aIVal.toLowerCase();
      const bVal =
        !isNaN(parseFloat(bIVal)) && isFinite(bIVal)
          ? bIVal
          : bIVal.toLowerCase();
      if (aVal < bVal) {
        return col.direction ? -1 : 1;
      }
      if (aVal > bVal) {
        return col.direction ? 1 : -1;
      }
      return 0;
    });
    return newRows;
  };

  const cancelFilter = () => {
    setRows(props.rows);
  };

  const handleTextFilter = (filter: any) => {
    if (filter) {
      let newTextFilters = textFilters;
      if (!filter.filterBy) {
        const index = newTextFilters.findIndex(
          (f) => f.col.dataField === filter.col.dataField
        );
        if (index > -1) {
          textFilters.splice(index, 1);
        }
      } else {
        const newFilter = newTextFilters.find(
          (f) => f.col.dataField === filter.col.dataField
        );
        if (newFilter) {
          newFilter.filterBy = filter.filterBy;
        } else {
          newTextFilters.push(filter);
        }
      }
      setTextFilters(newTextFilters);
      let newRows = filterRowsByTextFilters(newTextFilters, props.rows);
      if (multiSelectFilters.length) {
        newRows = filterByMultiSelectFilters(multiSelectFilters, newRows);
      }
      if (sortCol) {
        newRows = doSort(sortCol, newRows);
      }
      setRows([...newRows]);
    }
  };

  const filterRowsByTextFilters = (filters: any, rows: any) => {
    let newRows = rows;
    if (filters && filters.length) {
      filters.forEach((f) => {
        newRows = newRows.filter((r) => {

          if (r && r[f.col.dataField]) {
            let valueAsString = r[f.col.dataField].toString();

            return (
              valueAsString.toLowerCase().indexOf(f.filterBy.toLowerCase()) >
              -1
            );
          }

          return -1;
        });
      });
    }
    return newRows;
  };

  const handleMultiSelectFilterChange = (filter: any) => {
    if (filter) {
      let newMultiSelectFilters = multiSelectFilters;
      if (!filter.selectedFilters.length) {
        const index = newMultiSelectFilters.findIndex(
          (f) => f.col.dataField === filter.col.dataField
        );
        if (index > -1) {
          newMultiSelectFilters.splice(index, 1);
        }
      } else {
        const newFilter = newMultiSelectFilters.find(
          (f) => f.col.dataField === filter.col.dataField
        );
        if (newFilter) {
          newFilter.selectedFilters = filter.selectedFilters;
        } else {
          newMultiSelectFilters.push(filter);
        }
      }
      setMultiSelectFilters(newMultiSelectFilters);
      let newRows = filterByMultiSelectFilters(
        newMultiSelectFilters,
        props.rows
      );
      if (textFilters.length) {
        newRows = filterRowsByTextFilters(textFilters, newRows);
      }
      if (sortCol) {
        newRows = doSort(sortCol, newRows);
      }
      setRows([...newRows]);
    }
  };

  const filterByMultiSelectFilters = (filters: any, rows: any) => {
    let newRows = rows;
    if (filters && filters.length) {
      filters.forEach((f) => {
        let orRows = Array();
        f.selectedFilters.forEach((s) => {
          let tempRows = newRows.filter((r) => {
            return r[f.col.dataField] === s.name;
          });
          tempRows.forEach((r) => {
            orRows.push(r);
          });
        });
        newRows = orRows;
      });
    }
    return newRows;
  };

  const [width, setWidth] = useState(window.innerWidth);
  const breakpoint = 620;

  useEffect(() => {
    /* Inside of a "useEffect" hook add an event listener that updates
       the "width" state variable when the window size changes */
    window.addEventListener("resize", () => setWidth(window.innerWidth));

    /* passing an empty array as the dependencies of the effect will cause this
       effect to only run when the component mounts, and not each time it updates.
       We only want the listener to be added once */
  }, []);
  if (props.loading) {
    return <span>Načítám ...</span>;
  }

  if (width < breakpoint)
    return (
      <div>
        {deleteConfirmationVisible && (
          <ConfirmationPopup
            key="ConfirmationPopup"
            handleConfirmation={() => {
              if (activeRow && props.onDeleteRow) {
                props.onDeleteRow(activeRow);
              }
              setDeleteConfirmationVisible(false);
              setActiveRow(null);
            }}
            handleClose={() => {
              setDeleteConfirmationVisible(false);
            }}
            header={t('General.GridOperations.DeleteConfirmationHeader')}
            text={t('General.GridOperations.DeleteConfirmationMessage')}
          />
        )}
        {rows.map((row) => {
          return (
            <div className="mb-3 card">
              <div className="card-header">
                {(props.showEditButton || props.showDeleteButton) && (
                  <div className="text-right">
                    {props.showEditButton && (
                      <i
                        onClick={() => {
                          props.handleEditAction(row.id);
                        }}
                        className="fas fa-pencil-alt action-button"
                        style={{ marginRight: "10px", color: "green" }}
                      ></i>
                    )}
                    {props.showDeleteButton && (
                      <i
                        className="fas fa-trash action-button"
                        style={{ color: "purple" }}
                        onClick={() => {
                          setActiveRow(row);
                          setDeleteConfirmationVisible(true);
                        }}
                      ></i>
                    )}
                  </div>
                )}
              </div>
              <div className="card-body">
                {cols.map((col) => {
                  return (
                    <TextItem label={col.name} value={row[col.dataField]} />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  else
    return (
      <div
        className={`${props.additionalClassName
          ? `gridTable ${props.additionalClassName}`
          : `gridTable`
          }`}
      >
        {deleteConfirmationVisible && (
          <ConfirmationPopup
            key="ConfirmationPopup"
            handleConfirmation={() => {
              if (activeRow && props.onDeleteRow) {
                props.onDeleteRow(activeRow);
              }
              setDeleteConfirmationVisible(false);
              setActiveRow(null);
            }}
            handleClose={() => {
              setDeleteConfirmationVisible(false);
            }}
            header={t('General.DeletionPopup.Confirmation')}
            text={t('General.DeletionPopup.Message')}
          />
        )}
        <table className="table table-striped table-hover table-bordered table-sm tableStickyHeader">
          <thead>
            <tr>
              {cols.map((col) => {
                //return <th scope="col">{col.name}</th>;
                return (
                  <DataGridHeaderItem
                    col={col}
                    onSort={handleSort}
                    currSortCol={sortCol}
                    onTextFilter={handleTextFilter}
                    onMultiSelectFilterChange={handleMultiSelectFilterChange}
                    onCancelFilter={cancelFilter}
                  />
                );
              })}

              {(props.showDeleteButton || props.showEditButton) && (
                <th scope="col">{t('General.operation')}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row: any) => {
              return (
                <tr>
                  {props.cols.map((col: any) => {
                    if (col.isLinkToItem) {
                      return (
                        <td className={`${"dataFieldCell"}_${col.dataField}`}>
                          <a href="#">{row[col.dataField]}</a>
                        </td>
                      );
                    } else if (col.fieldType === "datetime") {
                      let val = row[col.dataField];
                      let formattedValue;
                      if (val) {
                        formattedValue = moment(val).format(
                          "DD.MM.YYYY"
                        );
                      }
                      return <td>{formattedValue}</td>;
                    } else if (col.fieldType === "number" || col.fieldType === "money") {
                      return (
                        <td className={`${"dataFieldCell"}_${col.dataField}`}>
                          <NumericFormat
                            value={row[col.dataField]}
                            decimalScale={2}
                            displayType={"text"}
                            decimalSeparator={','}
                          />
                        </td>
                      );

                    } else if (col.fieldType === 'numberWithGreenForPositiveAndRedForNegative') {

                      let backgroundColor = row[col.dataField] <= 0 ? '#fbc9c9' : '#dcffdc';
                      if (row[col.dataField] == 0) {
                        backgroundColor = 'transparent';
                      }

                      return (
                        <td style={{ backgroundColor: backgroundColor }} className={`${"dataFieldCell"}_${col.dataField}`}>
                          <NumericFormat
                            value={row[col.dataField]}
                            decimalScale={2}
                            displayType={"text"}
                            decimalSeparator={','}
                          />
                        </td>
                      );
                    }
                    else if (col.fieldType === "boolean") {
                      return (
                        <td className={`${"dataFieldCell"}_${col.dataField}`}>
                          {!!row[col.dataField] ? t('General.Values.True') : t('General.Values.False')}
                        </td>
                      );
                    }
                    else if (col.fieldType === "multi") {
                      let value: string[] = [];
                      let options = row[col.dataField];
                      if (options) {
                        options.forEach((o: any) => {
                          value.push(o[col.displayField]);
                        });
                      }
                      return (<td className={`${"dataFieldCell"}_${col.dataField}`}>{value.join(", ")}</td>)
                    } else if (col.dataField === "colorName") {
                      var isPictureField = row["isPicture"];
                      if (!isPictureField) {
                        return (
                          <td style={{ textAlign: "center" }}>
                            <a href="#" style={{ display: "inline-flex" }}>
                              <div
                                className="colorColumn"
                                style={{
                                  margin: "auto",
                                  width: "100px",
                                  height: "32px",
                                  backgroundColor: row["rgbColor"],
                                }}
                              >
                                <div className="colorTextValue">
                                  {row[col.dataField]}
                                </div>
                              </div>
                              {props.showCustomButton && (
                                <input
                                  type="button"
                                  value={props.customButtonText}
                                  onClick={() => {
                                    props.handleCustomAction(row.id);
                                  }}
                                  className="btn btn-sm btn-primary"
                                />
                              )}
                            </a>
                          </td>
                        );
                      } else {
                        let pictureName =
                          row["pictureName"] ??
                          `odstin_${row.name}_${row.id}.jpg`;
                        return (
                          <>
                            <Picture fileName={pictureName} />
                            <Button
                              onClick={() => {
                                var fileName = pictureName;
                                props.setPictureUploadPopupData(
                                  row["id"],
                                  fileName
                                );
                              }}
                              text={t('General.GridOperations.UploadImage')}
                              id={
                                row["pictureName"] ??
                                `odstin_${row["name"]}_${row["id"]}.jpg`
                              }
                            />
                          </>
                        );
                      }
                    } else {
                      return (
                        <td className={`${"dataFieldCell"}_${col.dataField}`}>
                          {row[col.dataField] ? row[col.dataField].toString() : ''}
                        </td>
                      );
                    }
                  })}

                  {(props.showEditButton || props.showDeleteButton) && (
                    <td className="text-center">
                      {props.showEditButton && (
                        <i
                          onClick={() => {
                            props.handleEditAction(row.id);
                          }}
                          className="fas fa-pencil-alt action-button"
                          style={{ marginRight: "10px", color: "green" }}
                        ></i>
                      )}
                      {props.showDeleteButton && (
                        <i
                          // onClick={() => {
                          //   props.handleDeleteAction(row.id);
                          // }}
                          className="fas fa-trash action-button"
                          style={{ color: "purple" }}
                          onClick={() => {
                            setActiveRow(row);
                            setDeleteConfirmationVisible(true);
                          }}
                        ></i>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
};

export default DataGrid;
